/**
 * Add/Edit modal for resident todos (spec §7). Renders through the house
 * `UnifiedModal` with `variant="sheet"` — a bottom sheet on mobile, and, since
 * UnifiedModal downgrades sheet → dialog at the desktop breakpoint, a centered
 * dialog on desktop web (`dialogMaxWidth` keeps the 420px the hand-rolled card
 * used to have). UnifiedModal owns the screen-level layer (its own transparent
 * RN `Modal`, which is what lets the section mount this mid-page inside
 * HomeScreen's ScrollView), the scrim, swipe-to-dismiss, Android hardware
 * back, and keyboard avoidance, so this file is only the form.
 *
 * `scrollable` matters here: the sheet caps at 90% of the screen, and the iOS
 * date spinner plus an open keyboard easily overflow that — the body has to
 * scroll rather than clip the notes field.
 *
 * Mode derives from `todo`: null → create, non-null → edit (prefilled, two-tap
 * destructive Delete). The date field follows the maintenance flows' platform
 * split: web renders the picker inline, iOS toggles an inline spinner — which
 * needs its own Done, see `showPickerDone` — and Android mounts the system
 * dialog on demand.
 */
import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useWebDateInputStyle } from '@/hooks/shared/useWebDateInputStyle';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { onColor } from '@/constants/shared/theme';
import {
  formatDayHeading,
  parseDateKey,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';

export interface TodoFormValues {
  title: string;
  dueDate: string;
  notes?: string;
}

interface TodoFormModalProps {
  visible: boolean;
  /** null → create mode; a todo → edit mode (prefilled). */
  todo: ResidentTodo | null;
  /** Create-mode due date ('YYYY-MM-DD') — the calendar's active day. */
  initialDateKey: string;
  onSave: (values: TodoFormValues) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  onClose: () => void;
}

export const TodoFormModal: React.FC<TodoFormModalProps> = ({
  visible,
  todo,
  initialDateKey,
  onSave,
  onDelete,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [dateKey, setDateKey] = useState(initialDateKey);
  const [notes, setNotes] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever the modal opens or its SUBJECT changes.
  //
  // Keyed on `todo?.id`, deliberately not on `todo`: row identity churns
  // underneath us. residentTodosApi's optimistic `updateQueryData` patch
  // rewrites the edited row (Immer → new object), and `patchResult.undo()`
  // rewrites it again when the request fails. Depending on the object would
  // re-run this effect twice on a rejected save, reverting the user's typed
  // edits to server values and clearing the inline error the catch just set —
  // the exact opposite of the spec's "stays open with an inline error and
  // intact form state". `todo?.id` still flips on create↔edit, on
  // todo→different-todo, and `visible` still covers close→reopen.
  useEffect(() => {
    if (visible) {
      setTitle(todo?.title ?? '');
      setDateKey(todo?.dueDate ?? initialDateKey);
      setNotes(todo?.notes ?? '');
      setShowPicker(false);
      setBusy(false);
      setConfirmingDelete(false);
      setError(null);
    }
    // `todo` is intentionally excluded — see above; its identity churns on
    // every optimistic patch, but only a change of `todo.id` is a new subject.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, todo?.id, initialDateKey]);

  const { colors: themeColors } = useThemeColors();
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const webDateInputStyle = useWebDateInputStyle();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      // Merged onto UnifiedModal's scroll content container, which already
      // supplies the horizontal padding; this only adds the field rhythm and
      // clears the header's bottom border.
      body: {
        paddingTop: space.lg,
        gap: space.lg,
      },
      footerContent: {
        gap: space.sm,
      },
      fieldLabel: {
        ...type.small,
        color: colors.text.muted,
      },
      input: {
        ...type.body,
        color: colors.text.primary,
        backgroundColor: semantic.background.input,
        borderColor: semantic.border.input,
        borderWidth: 1,
        borderRadius: radius.md,
        paddingHorizontal: space.md,
        paddingVertical: space.sm,
      },
      notesInput: {
        minHeight: 72,
        textAlignVertical: 'top',
      },
      dateField: {
        backgroundColor: semantic.background.input,
        borderColor: semantic.border.input,
        borderWidth: 1,
        borderRadius: radius.md,
        paddingHorizontal: space.md,
        paddingVertical: space.sm,
      },
      dateFieldText: {
        ...type.body,
        color: colors.text.primary,
      },
      // Tighter than the body's `lg` field rhythm so the Done affordance reads
      // as part of the picker rather than as another form field.
      pickerGroup: {
        gap: space.xs,
      },
      pickerDone: {
        alignSelf: 'flex-end',
        paddingHorizontal: space.sm,
        paddingVertical: space.xs,
      },
      pickerDoneText: {
        ...type.button,
        // `primaryHover`, not `primary`. As an INK on the sheet's bg.modal,
        // primary measures 3.99:1 in dark — under the 4.5:1 floor — while
        // primaryHover is the AA-safe blue in both modes (6.69:1 light,
        // 5.96:1 dark). The maintenance picker this flow was modeled on
        // still reaches for `primary` here and has the same gap.
        color: semantic.interactive.primaryHover,
      },
      errorText: {
        ...type.small,
        color: semantic.status.errorText,
      },
      actionsRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: space.md,
      },
      cancelText: {
        ...type.body,
        color: colors.text.secondary,
      },
      // A padded box pulled back by the same amounts: the word stays put, the
      // hover tint gets room around it, and the padding is the 8pt reach the
      // hitSlop used to give.
      cancelButton: {
        paddingHorizontal: space.sm,
        paddingVertical: space.xs,
        marginHorizontal: -space.sm,
        marginVertical: -space.xs,
        borderRadius: radius.md,
      },
      saveButton: {
        backgroundColor: semantic.interactive.primary,
        borderRadius: radius.md,
        paddingHorizontal: space.lg,
        paddingVertical: space.sm,
      },
      saveButtonDisabled: {
        opacity: 0.5,
      },
      saveText: {
        // `type.button` (14/600) is the primary scale's button-label token —
        // the brief named `bodyMedium`, which lives only on MOBILE_TYPOGRAPHY,
        // not on what useTypography() returns. Matches the planner's other
        // button inks ("+ Add", Retry).
        ...type.button,
        color: onColor(semantic.interactive.primary),
      },
      deleteButton: {
        backgroundColor: semantic.interactive.danger,
        borderRadius: radius.md,
        paddingHorizontal: space.lg,
        paddingVertical: space.sm,
        marginRight: 'auto',
      },
      deleteText: {
        ...type.button,
        color: onColor(semantic.interactive.danger),
      },
    }),
  );

  // No `if (!visible) return null` guard: UnifiedModal has to stay mounted
  // through its own exit animation, and it already renders nothing once that
  // finishes.
  const canSave = title.trim().length > 0 && !busy;

  // Only the iOS wheel needs an explicit dismiss. It renders INLINE and fires
  // `onChange` on every tick of the wheel — there is no terminal "user picked"
  // event to close on, so without this the picker stays open forever once
  // opened. Android's `default` display is a system dialog that reports a
  // single selection (closed in onChange below), and on web the picker is
  // permanently mounted and never had an open/closed state to begin with.
  const showPickerDone = Platform.OS === 'ios' && showPicker;

  const handleSave = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      const trimmedNotes = notes.trim();
      await onSave({
        title: title.trim(),
        dueDate: dateKey,
        notes: trimmedNotes.length > 0 ? trimmedNotes : undefined,
      });
      onClose();
    } catch {
      setError("Couldn't save. Please try again.");
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!todo || busy) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onDelete(todo.id);
      onClose();
    } catch {
      setError("Couldn't delete. Please try again.");
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <UnifiedModal
      visible={visible}
      onClose={onClose}
      variant="sheet"
      title={todo ? 'Edit todo' : 'New todo'}
      dialogMaxWidth={420}
      avoidKeyboard
      scrollable
      contentContainerStyle={styles.body}
      footer={
        // The error lives in the footer, not at the end of the body: the body
        // scrolls, and a save failure that scrolls out of view alongside the
        // Save button the user just pressed reads as nothing having happened.
        <View style={styles.footerContent}>
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          <View style={styles.actionsRow}>
            {todo ? (
              <Hoverable
                hover="button"
                style={styles.deleteButton}
                onPress={handleDelete}
                accessibilityRole="button"
                accessibilityLabel={confirmingDelete ? 'Confirm delete todo' : 'Delete todo'}
                accessibilityState={{ disabled: busy }}
              >
                <Text style={styles.deleteText}>
                  {confirmingDelete ? 'Confirm delete' : 'Delete'}
                </Text>
              </Hoverable>
            ) : null}
            <Hoverable
              hover="row"
              style={styles.cancelButton}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cancel todo form"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Hoverable>
            <Hoverable
              hover="button"
              style={[styles.saveButton, !canSave && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={!canSave}
              accessibilityRole="button"
              accessibilityLabel="Save todo"
              accessibilityState={{ disabled: !canSave }}
            >
              <Text style={styles.saveText}>Save</Text>
            </Hoverable>
          </View>
        </View>
      }
    >
      <Text style={styles.fieldLabel}>Title</Text>
      <TextInput
        style={styles.input}
        value={title}
        onChangeText={setTitle}
        placeholder="What needs doing?"
        placeholderTextColor={themeColors.text.dimmer}
        // Server column limits — cap here so a long paste can't round-trip
        // into a generic 422.
        maxLength={255}
        autoFocus={!todo}
        accessibilityLabel="Todo title"
      />

      <Text style={styles.fieldLabel}>Due date</Text>
      <Hoverable
        hover="field"
        style={styles.dateField}
        onPress={() => setShowPicker(s => !s)}
        accessibilityRole="button"
        accessibilityLabel={`Due date: ${formatDayHeading(parseDateKey(dateKey))}`}
      >
        <Text style={styles.dateFieldText}>{formatDayHeading(parseDateKey(dateKey))}</Text>
      </Hoverable>
      {(Platform.OS === 'web' || showPicker) && (
        <View style={styles.pickerGroup}>
          {/* Above the wheel, not below it: the wheel is ~200pt tall inside a
              body that scrolls, so a Done placed under it can open below the
              fold — while up here it's guaranteed visible the moment the tap
              that opened the picker lands. Also matches the iOS accessory-
              toolbar convention the maintenance picker follows. */}
          {showPickerDone && (
            <Hoverable
              hover="link"
              style={styles.pickerDone}
              onPress={() => setShowPicker(false)}
              accessibilityRole="button"
              accessibilityLabel="Done choosing due date"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              {({ textStyle }) => <Text style={[styles.pickerDoneText, textStyle]}>Done</Text>}
            </Hoverable>
          )}
          <DateTimePicker
            value={parseDateKey(dateKey)}
            mode="date"
            display={Platform.OS === 'ios' ? 'spinner' : 'default'}
            // Web only (undefined on native): the raw <input> the web shim
            // renders takes the browser's face and colors unless told otherwise.
            style={webDateInputStyle}
            onChange={(_event: unknown, date?: Date) => {
              if (Platform.OS === 'android') setShowPicker(false);
              if (date) setDateKey(toDateKey(date));
            }}
          />
        </View>
      )}

      <Text style={styles.fieldLabel}>Notes (optional)</Text>
      <TextInput
        style={[styles.input, styles.notesInput]}
        value={notes}
        onChangeText={setNotes}
        maxLength={2000}
        multiline
        accessibilityLabel="Todo notes"
      />
    </UnifiedModal>
  );
};

TodoFormModal.displayName = 'TodoFormModal';
